import { X, UserPlus, Mail, User, Shield } from "lucide-react";

const InviteUserModal = ({
  isOpen,
  onClose,
  onInvite,
  loading,
  newUser,
  setNewUser,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-[9999] p-4">
      {/* Glass Morphism Modal */}
      <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-2xl w-full max-w-md relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5 rounded-3xl"></div>
        <div className="absolute -top-10 -right-10 w-20 h-20 bg-[#B8460E]/10 rounded-full blur-2xl animate-pulse"></div>
        <div
          className="absolute -bottom-10 -left-10 w-20 h-20 bg-blue-500/10 rounded-full blur-2xl animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>

        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 translate-x-[-200%] hover:translate-x-[200%] transition-transform duration-2000 pointer-events-none rounded-3xl"></div>

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#B8460E]/20 rounded-xl flex items-center justify-center">
              <UserPlus className="h-5 w-5 text-[#B8460E]" />
            </div>
            <h2 className="text-xl font-bold text-white drop-shadow-lg font-display">
              Invite Team Member
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-white/60 hover:text-white hover:bg-white/10 transition-all duration-300 p-2 rounded-xl disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={onInvite} className="p-6 space-y-5 relative z-10">
          {/* Username Field */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Username
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3.5 h-4 w-4 text-white/60" />
              <input
                type="text"
                value={newUser.username}
                onChange={(e) =>
                  setNewUser({ ...newUser, username: e.target.value })
                }
                className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300 disabled:opacity-50"
                placeholder="Enter username"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 h-4 w-4 text-white/60" />
              <input
                type="email"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
                className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300 disabled:opacity-50"
                placeholder="Enter email address"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Admin Checkbox */}
          <div className="flex items-center space-x-3 p-4 bg-white/5 rounded-xl border border-white/10">
            <div className="relative">
              <input
                type="checkbox"
                id="admin-checkbox"
                checked={newUser.is_admin}
                onChange={(e) =>
                  setNewUser({ ...newUser, is_admin: e.target.checked })
                }
                disabled={loading}
                className="w-5 h-5 text-[#B8460E] bg-white/10 border-white/20 rounded focus:ring-[#B8460E] focus:ring-2 disabled:opacity-50"
              />
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-white/60" />
              <label
                htmlFor="admin-checkbox"
                className="text-sm font-medium text-white/90 cursor-pointer"
              >
                Grant Administrator Privileges
              </label>
            </div>
          </div>

          {/* Info Note */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
            <p className="text-xs text-blue-300">
              📧 An invitation email with password setup instructions will be
              sent automatically. Admin-created accounts are auto-verified.
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 text-sm font-medium text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl transition-all duration-300 hover:scale-105 backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 text-sm font-medium text-white bg-[#B8460E] hover:bg-[#8B3A0E] border border-[#B8460E]/50 rounded-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Sending Invite...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4" />
                  Send Invitation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUserModal;
